import React, { DispatchWithoutAction, useEffect } from "react";
import { IComponentParent, TriggeredHook } from "sonobello.utilities.react";

import { AppContextProps } from "../../../../AppContext";
import { LeadResult } from "../../../../dtos/LeadResult";
import { ISomethingWentWrongRouter, IThankYouViewRouter } from "../../../Routing/Types/IRouter";
import IReservation from "../../../Types/IReservation";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import { IUseGetCalendarEventRequest } from "../Hooks/UseGetCalendarEventUrl";

/** The expected result of the {@link IConfirmationStepWrapperProps.onConfirmedAppointment} hook. */
export interface IConfirmedAppointmentResponse {
  appointment: { startTimeUtc: string; endTimeUtc: string; id: string };
  leadResult: LeadResult;
}

export interface IConfirmationStepWrapperProps {
  /** @see {@link AppContextProps.customerCState} */
  customer: AppContextProps["customerCState"];
  /** @see {@link AppContextProps.leadIdCState} */
  leadId: AppContextProps["leadIdCState"];
  /** The booking reservation to be confirmed. */
  reservation: IReservation;
  /** The view router for the 'Thank You' and 'Something Went Wrong' views. */
  router: IThankYouViewRouter & ISomethingWentWrongRouter;
}

export interface IConfirmationStepWrapperExperimentProps {
  /** Dispatches that the appointment has been successfully booked. */
  recordBookedAppointment: DispatchWithoutAction;
}

export interface IConfirmationStepWrapperConfig {
  /** Dispatches the confirmed appointment details */
  useConfirmAppointment: TriggeredHook<IConfirmedAppointmentResponse, IReservation>;
  /** Obtains the calendar event url for the given appointment. */
  useGetCalendarEventUrl: TriggeredHook<string, IUseGetCalendarEventRequest>;
}

/** A step wrapper which on 'next step' clicked confirms the reservation as an appointment and obtains the calendar
 * event url before sending the user to the 'Thank You' view.
 */
const ConfirmationStepWrapper: React.FC<
  IConfirmationStepWrapperProps &
    IConfirmationStepWrapperConfig &
    IConfirmationStepWrapperExperimentProps &
    IStepWrapperButtonProps &
    IComponentParent
> = ({
  children,
  customer,
  leadId,
  reservation,
  router,
  recordBookedAppointment,
  useConfirmAppointment,
  useGetCalendarEventUrl,
  NextButtonDesktop,
  NextButtonMobile
}) => {
  const {
    result: confirmedAppointment,
    execute: confirmAppointment,
    error: confirmAppointmentError,
    isLoading: isAppointmentLoading
  } = useConfirmAppointment();

  const {
    result: calendarEventUrl,
    execute: getCalendarEventUrl,
    isLoading: isCalendarEventUrlLoading,
    error: calendarEventUrlError
  } = useGetCalendarEventUrl();

  useEffect(() => {
    if (confirmedAppointment && (calendarEventUrl || calendarEventUrlError))
      router.goToThankYouView({
        appointmentInfo: {
          center: reservation.center,
          startTimeUtc: confirmedAppointment.appointment.startTimeUtc,
          endTimeUtc: confirmedAppointment.appointment.endTimeUtc
        },
        calendarEventUrl: calendarEventUrl || null
      });
  }, [confirmedAppointment, calendarEventUrlError, calendarEventUrl, router]);

  useEffect(() => {
    if (confirmAppointmentError) router.goToSomethingWentWrongView();
  }, [confirmAppointmentError]);

  useEffect(() => {
    if (confirmedAppointment) {
      recordBookedAppointment();
      getCalendarEventUrl({ customerId: customer.id, leadId, appointmentId: confirmedAppointment.appointment.id });
    }
  }, [confirmedAppointment]);

  return (
    <StepWrapper
      nextButtonLabel="Book This Appointment"
      nextButtonLoading={isAppointmentLoading || isCalendarEventUrlLoading}
      onNext={() => confirmAppointment(reservation)}
      NextButtonDesktop={NextButtonDesktop}
      NextButtonMobile={NextButtonMobile}
    >
      {children}
    </StepWrapper>
  );
};

export default ConfirmationStepWrapper;
